import type { EditorView } from "prosemirror-view";
import { iconButton } from "./dom";

// A card reference is the inline link (the `card_link` node) or a section (the
// `card_embed` node).
type MenuMode = "inline" | "section";

const MODES: { value: MenuMode; label: string }[] = [
  { value: "inline", label: "Inline link" },
  { value: "section", label: "Section" },
];

const MENU_ID = "card-link-mode-menu";

type MenuTarget = {
  view: EditorView;
  getPos: () => number | undefined;
  uuid: string;
  current: MenuMode;
};

// The `⋯` button of an inline link and of a section. It opens the menu that
// changes the display of the card reference.
export function cardMenuButton(target: MenuTarget): HTMLButtonElement {
  const menuButton = iconButton("menu", "Card display", () => openCardLinkMenu(menuButton, target));
  return menuButton;
}

function openCardLinkMenu(anchor: HTMLElement, target: MenuTarget): void {
  document.getElementById(MENU_ID)?.remove();
  const { view, getPos, uuid, current } = target;

  const menu = document.createElement("ul");
  menu.id = MENU_ID;
  menu.className = "link-mode-menu";
  const rect = anchor.getBoundingClientRect();
  menu.style.left = `${rect.left}px`;
  menu.style.top = `${rect.bottom + 4}px`;

  for (const { value, label } of MODES) {
    const li = document.createElement("li");
    li.className =
      "link-mode-menu__item" + (value === current ? " link-mode-menu__item--active" : "");
    li.textContent = label;
    li.addEventListener("mousedown", (e) => {
      e.preventDefault();
      menu.remove();
      applyCardLinkMode(view, getPos, uuid, current, value);
    });
    menu.appendChild(li);
  }

  const close = (e: MouseEvent) => {
    if (!menu.contains(e.target as Node)) {
      menu.remove();
      document.removeEventListener("mousedown", close, true);
    }
  };
  document.addEventListener("mousedown", close, true);
  document.body.appendChild(menu);
}

// Change a card reference between the inline link and the section. The two
// nodes are of different types, so the change also changes the block around
// the node: a link becomes a section by a split of its textblock, and a section
// becomes a paragraph that holds the link.
function applyCardLinkMode(
  view: EditorView,
  getPos: () => number | undefined,
  uuid: string,
  current: MenuMode,
  target: MenuMode
): void {
  if (target === current) return;
  const pos = getPos();
  if (pos == null) return;
  const { schema } = view.state;
  const tr = view.state.tr;

  if (target === "inline") {
    // The section is a block atom of size 1.
    const link = schema.nodes["card_link"]!.create({ uuid });
    tr.replaceWith(pos, pos + 1, schema.nodes["paragraph"]!.create(null, link));
  } else {
    // replaceRangeWith splits the textblock, so the block can be at this
    // inline position.
    tr.replaceRangeWith(pos, pos + 1, schema.nodes["card_embed"]!.create({ uuid }));
  }

  view.dispatch(tr);
  view.focus();
}
